import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { resizePublicImage } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Store } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';

interface ImageContentProps {
  images: string[];
  productName: string;
}

function ImageContent({ images, productName }: ImageContentProps) {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const [hoverErrors, setHoverErrors] = useState<Set<number>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);

  const firstImage = images[0];
  const hasMultiple = images.length > 1;
  const { indices: validHoverIndices, images: validHoverImages } =
    useMemo(() => {
      const indices: number[] = [];
      const urls: string[] = [];
      for (let i = 0; i < images.length; i++) {
        if (!hoverErrors.has(i)) {
          indices.push(i);
          urls.push(images[i]);
        }
      }
      return { indices, images: urls };
    }, [images, hoverErrors]);
  const validCount = validHoverImages.length;
  const displayIndex =
    validCount > 0 ? Math.min(currentIndex, validCount - 1) : 0;

  useEffect(() => {
    setCurrentIndex((i) => Math.min(i, Math.max(0, validCount - 1)));
  }, [validCount]);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + validCount) % validCount);
  }, [validCount]);

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % validCount);
  }, [validCount]);

  const handleHoverImageError = useCallback((index: number) => {
    setHoverErrors((prev) => new Set(prev).add(index));
  }, []);

  if (!firstImage || imageError) {
    if (imageError && firstImage) {
      console.log('[imgproxy] Image load error (thumbnail):', {
        imageUrl: firstImage,
        productName,
      });
    }
    return (
      <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded border border-gray-200 bg-gray-100 text-gray-400">
        <Store className="h-6 w-6" />
      </div>
    );
  }

  return (
    <HoverCard openDelay={0} closeDelay={0}>
      <HoverCardTrigger asChild>
        <div className="relative h-8 w-8 cursor-pointer overflow-hidden rounded border border-gray-200 bg-gray-100">
          <img
            src={resizePublicImage(firstImage, 32, 32)}
            alt={productName}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              console.log('[imgproxy] Image onError (thumbnail):', {
                src: target.src,
                imageUrl: firstImage,
                productName,
              });
              setImageError(true);
            }}
          />
        </div>
      </HoverCardTrigger>
      {validCount > 0 && (
        <HoverCardContent
          className="relative flex h-48 w-48 flex-col border-0 p-0"
          align="start"
          side="right"
          alignOffset={30}
        >
          <div className="relative flex flex-1 items-center justify-center overflow-hidden rounded bg-gray-50">
            {validHoverImages.map((imageUrl, i) => (
              <img
                key={validHoverIndices[i]}
                src={resizePublicImage(imageUrl, 192, 192)}
                alt={`${productName} ${i + 1}`}
                className={cn(
                  'absolute inset-0 h-full w-full rounded object-contain transition-opacity duration-150',
                  i === displayIndex
                    ? 'opacity-100'
                    : 'pointer-events-none opacity-0'
                )}
                loading="eager"
                onError={() => handleHoverImageError(validHoverIndices[i])}
              />
            ))}
          </div>
          {hasMultiple && validCount > 1 && (
            <div className="flex items-center justify-between gap-1 border-t border-gray-100 bg-white px-1 py-1.5">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={(e) => {
                  e.preventDefault();
                  goPrev();
                }}
                aria-label={t('general.previous')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[3ch] text-center text-xs text-muted-foreground">
                {displayIndex + 1} / {validCount}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={(e) => {
                  e.preventDefault();
                  goNext();
                }}
                aria-label={t('general.next')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </HoverCardContent>
      )}
    </HoverCard>
  );
}

const useColumnDefImages = (): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const accessorKey = 'images';
  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'Images',
      header: () => <div>{t('kpi.images')}</div>,
      cell: ({ row }) => {
        const isParent = row.subRows.length > 0;

        return (
          <div
            className={`flex w-full flex-col items-center space-y-2 ${!isParent ? 'pl-4' : ''}`}
          >
            {row.original.activeVersion.images?.length ? (
              <ImageContent
                images={row.original.activeVersion.images}
                productName={row.original.activeVersion.name}
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded border border-gray-200 bg-gray-100 text-gray-400">
                <Store className="h-6 w-6" />
              </div>
            )}
          </div>
        );
      },
      size: 60,
    }),
    [t]
  );
};

export default useColumnDefImages;
